import React, { useState, useEffect } from 'react';
import { Stack, Paper, Grid, TextField, Button, Typography, Checkbox, FormControlLabel, FormControl, InputLabel, MenuItem, Select } from '@mui/material/';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs, { Dayjs } from 'dayjs';

//components imports
import AlertInfo from '../../../dataObjects/AlertInfo';
import AlertInfoDialog from '../../../components/elements/AlertInfoDialog';
import { GenericFormObject } from '../dataObjects/GenericFormObject';

interface FormDataProps {
    formData: GenericFormObject;
    closeGenericForm: () => void;
}

const ChangeTreasurerForm = (props: FormDataProps): JSX.Element => {

    /*Error Handling*/
    const [isAlertInfoDialogOpen, setAlertInfoDialog] = useState(false);
    const [alertInfo, setAlertInfo] = useState<AlertInfo>();

    const [form, setForm] = useState<GenericFormObject>(props.formData);
    const [treasurerSalutation, setTreasurerSalutation] = useState<string>('');
    const [create202OBLIGLetter, setCreate202OBLIGLetter] = useState<boolean>(false);
    const [create218Letter, setCreate218Letter] = useState<boolean>(false);

    useEffect(() => {
        setForm(props.formData);
    }, [props.formData]);

    const closeAlertInfoDialog = () => {
        setAlertInfoDialog(false);
        if (alertInfo?.type === 'success') {
            props.closeGenericForm();
        }
    }

    const saveNewTreasurerInfo = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        const formData = new FormData(event.currentTarget);
        formData.append("cpfId", form.cpfID);
        formData.append("create202OBLIGLetter", create202OBLIGLetter.toString());
        formData.append("create218Letter", create218Letter.toString());
        formData.append("url", form.formIntakeBlobUrl);

        fetch(`/LLMFormProcessor/AppointTreasurer`,
            {
                method: 'POST',
                body: formData,
            })
            .then(result => result.json())
            .then(result => {
                if (result.isError) {
                    let info = {
                        type: 'error',
                        title: 'Error: AppointTreasurer',
                        errorMessage: result.errorMessage,
                        secondaryErrorMessage: result.secondaryErrorMessage
                    };
                    setAlertInfo(info);
                    setAlertInfoDialog(true);
                }
                else {
                    let info = {
                        type: 'success',
                        title: 'The New Treasurer Info Saved',
                        errorMessage: 'The T101 form was posted\nThe new treasurer info was saved\nThe draft archer letters were created in Archer.',
                        secondaryErrorMessage: ''
                    };
                    setAlertInfo(info);
                    setAlertInfoDialog(true);
                    //open profiles??
                }
            });
    }

    return (
        <>
            <form onSubmit={(event: React.FormEvent<HTMLFormElement>) => saveNewTreasurerInfo(event)}>
                <Paper sx={{ p: 3 }} >
                    <Grid item xs={12} marginTop={2} >
                        <Stack spacing={2}>
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DatePicker
                                    sx={{ minWidth: 100, width: 350 }}
                                    name="effectiveDate"
                                    label="T101 Received Date"
                                    defaultValue={dayjs()}
                                />
                            </LocalizationProvider>
                            <Typography variant="subtitle2" fontWeight={550} >New Treasurer Info</Typography>
                            <Stack direction="row" spacing={1}>
                                <FormControl sx={{ m: 1, minWidth: 120 }} size="small" error={treasurerSalutation === ''}>
                                    <InputLabel>Salutation</InputLabel>
                                    <Select
                                        name= "salutation"
                                        label="Salutation"
                                        value={treasurerSalutation}
                                        onChange={(e) => setTreasurerSalutation(e.target.value)}
                                        required
                                    >
                                        <MenuItem value={'Mr.'}>Mr.</MenuItem>
                                        <MenuItem value={'Ms.'}>Ms.</MenuItem>
                                        <MenuItem value={'Mx.'}>Mx.</MenuItem>
                                    </Select>
                                </FormControl>
                                <TextField
                                    margin="normal" 
                                    name="firstName"
                                    label="First Name"
                                    size="small"
                                    InputLabelProps={{ shrink: true }}
                                    defaultValue={form.newTreasurerName?.firstName}
                                    required
                                />
                                <TextField
                                    margin="normal"
                                    name="middleName"
                                    label="Middle Name"
                                    size="small"
                                    InputLabelProps={{ shrink: true }}
                                    defaultValue={form.newTreasurerName?.middleName}
                                />
                                <TextField
                                    margin="normal"
                                    name="lastName"
                                    label="Last Name"
                                    size="small"
                                    InputLabelProps={{ shrink: true }}
                                    defaultValue={form.newTreasurerName?.lastName}
                                    required
                                />
                                <FormControl sx={{ m: 1, minWidth: 120 }} size="small">
                                    <InputLabel>Suffix</InputLabel>
                                    <Select
                                        name="suffix"
                                        label="Suffix"
                                        defaultValue = ''
                                    >
                                        <MenuItem value={'II'}>II</MenuItem>
                                        <MenuItem value={'III'}>III</MenuItem>
                                        <MenuItem value={'IV'}>IV</MenuItem>
                                        <MenuItem value={'Jr.'}>Jr.</MenuItem>
                                        <MenuItem value={'Sr.'}>Sr.</MenuItem>
                                    </Select>
                                </FormControl>
                            </Stack>
                            <TextField
                                margin="normal"
                                name="streetAddress"
                                label="Street Address"
                                size="small"
                                InputLabelProps={{ shrink: true }}
                                defaultValue={form.treasurerAddress?.streetAddress}
                            />
                            <Stack direction="row" spacing={1}>
                                <TextField
                                    margin="normal"
                                    name="careOf"
                                    label="Care Of"
                                    size="small"
                                    InputLabelProps={{ shrink: true }}
                                    //defaultValue={form.treasurerCareOf}
                                />
                                <TextField
                                    margin="normal"
                                    name="city"
                                    label="City"
                                    size="small"
                                    InputLabelProps={{ shrink: true }}
                                    defaultValue={form.treasurerAddress?.city}
                                />
                                <TextField
                                    margin="normal"
                                    name="state"
                                    label="State"
                                    size="small"
                                    InputLabelProps={{ shrink: true }}
                                    defaultValue={form.treasurerAddress?.state}
                                />
                                <TextField
                                    margin="normal"
                                    name="zipCode"
                                    label="Zip Code"
                                    size="small"
                                    InputLabelProps={{ shrink: true }}
                                    defaultValue={form.treasurerAddress?.zipCode}
                                />
                            </Stack>
                            <TextField
                                margin="normal"
                                name="emailAddress"
                                label="Email Address"
                                size="small"
                                InputLabelProps={{ shrink: true }}
                                defaultValue={form.treasurerAddress?.emailAddress}
                            />
                            <TextField
                                margin="normal"
                                name="phoneNumber"
                                label="Phone Number"
                                size="small"
                                InputLabelProps={{ shrink: true }}
                                defaultValue={form.treasurerAddress?.phoneNumber}
                            />
                            <FormControlLabel
                                control={<Checkbox checked={create202OBLIGLetter} color="primary" onChange={(e) => setCreate202OBLIGLetter(e.target.checked)} />}
                                label="Create A Treasurer Resignation, Obligation Notification addressed to the old treasurer?"
                            />
                            <FormControlLabel
                                control={<Checkbox checked={create218Letter} color="primary" onChange={(e) => setCreate218Letter(e.target.checked)} />}
                                label={`Create A New Treasurer Acknowledgement Letter addressed to the new treasurer?`}
                            />
                        </Stack>
                    </Grid>
                </Paper>
                <Grid item xs={12} marginTop={3} marginBottom={3} >
                    <Stack direction="row" spacing={2} justifyContent={"flex-end"} marginTop={2} >
                        <Button variant="contained" color="warning" onClick={props.closeGenericForm}>Cancel</Button>
                        <Button variant="contained" type="submit">Save</Button>
                    </Stack>
                </Grid>
            </form>
            {
                alertInfo &&
                <AlertInfoDialog isAlertInfoDialogOpen={isAlertInfoDialogOpen} alertInfo={alertInfo} closeAlertInfoDialog={closeAlertInfoDialog}></AlertInfoDialog>
            }
        </>
    )
}

export default ChangeTreasurerForm;